import type { NervousSystem, RegionAnchor, RegionId } from './types';

export interface RegionInfo {
  id: RegionId;
  displayName: string;
  shortDescription: string;
  /** Which subdivision of the nervous system this structure belongs to. */
  system: NervousSystem;
  /**
   * Where the marker sits.
   *
   * Brain structures use approximate MNI coordinates as used in the neuroimaging
   * literature — conventional values, not measurements taken for this project,
   * so they can be checked against published atlases. Structures below the head
   * use the body frame (millimetres from the brain centroid), since MNI is a
   * brain template and does not extend to the cord or periphery.
   *
   * Right-sided coordinates are used throughout: the visualization shows one
   * marker per structure rather than a bilateral pair.
   *
   * These are illustrative placements at this visualization's scale, not
   * stereotactic targets.
   */
  anchor: RegionAnchor;
  /** Vertical label offset so nearby structures stay legible. */
  labelOffset?: number;
}

const mni = (x: number, y: number, z: number): RegionAnchor => ({ space: 'mni', coords: [x, y, z] });
const body = (x: number, y: number, z: number): RegionAnchor => ({
  space: 'body',
  coords: [x, y, z],
});

export const REGIONS: Record<RegionId, RegionInfo> = {
  // ── Central: brain ──────────────────────────────────────────────────────
  prefrontal_cortex: {
    id: 'prefrontal_cortex',
    displayName: 'Prefrontal Cortex',
    shortDescription: 'Executive function, decision-making, impulse control',
    system: 'central',
    anchor: mni(30, 50, 20),
    labelOffset: 0.1,
  },
  amygdala: {
    id: 'amygdala',
    displayName: 'Amygdala',
    shortDescription: 'Emotion processing, fear and threat response',
    system: 'central',
    anchor: mni(23, -5, -18),
    labelOffset: -0.02,
  },
  hippocampus: {
    id: 'hippocampus',
    displayName: 'Hippocampus',
    shortDescription: 'Memory formation and consolidation',
    system: 'central',
    anchor: mni(28, -22, -14),
    labelOffset: 0.06,
  },
  nucleus_accumbens: {
    id: 'nucleus_accumbens',
    displayName: 'Nucleus Accumbens',
    shortDescription: 'Reward processing, motivation, reinforcement',
    system: 'central',
    anchor: mni(10, 10, -8),
    labelOffset: 0.02,
  },
  vta: {
    id: 'vta',
    displayName: 'Ventral Tegmental Area',
    shortDescription: 'Dopamine synthesis, origin of the reward pathway',
    system: 'central',
    anchor: mni(5, -16, -14),
    labelOffset: -0.06,
  },
  raphe_nuclei: {
    id: 'raphe_nuclei',
    displayName: 'Raphe Nuclei',
    shortDescription: 'Serotonin synthesis and distribution',
    system: 'central',
    anchor: mni(2, -28, -20),
    labelOffset: -0.12,
  },
  locus_coeruleus: {
    id: 'locus_coeruleus',
    displayName: 'Locus Coeruleus',
    shortDescription: 'Main noradrenaline source; arousal and vigilance',
    system: 'central',
    anchor: mni(6, -38, -24),
    labelOffset: -0.18,
  },
  thalamus: {
    id: 'thalamus',
    displayName: 'Thalamus',
    shortDescription: 'Sensory relay and integration',
    system: 'central',
    anchor: mni(11, -19, 7),
    labelOffset: 0.12,
  },
  visual_cortex: {
    id: 'visual_cortex',
    displayName: 'Visual Cortex',
    shortDescription: 'Visual processing (occipital lobe)',
    system: 'central',
    anchor: mni(10, -90, 0),
    labelOffset: 0.08,
  },
  motor_cortex: {
    id: 'motor_cortex',
    displayName: 'Motor Cortex',
    shortDescription: 'Initiates voluntary movement via the corticospinal tract',
    system: 'central',
    anchor: mni(36, -20, 56),
    labelOffset: 0.16,
  },
  striatum: {
    id: 'striatum',
    displayName: 'Striatum',
    shortDescription: 'Movement control, habit formation, reward learning',
    system: 'central',
    anchor: mni(24, 4, 6),
    labelOffset: 0.04,
  },
  cerebellum: {
    id: 'cerebellum',
    displayName: 'Cerebellum',
    shortDescription: 'Motor coordination, balance, motor learning',
    system: 'central',
    anchor: mni(25, -60, -35),
    labelOffset: -0.14,
  },
  hypothalamus: {
    id: 'hypothalamus',
    displayName: 'Hypothalamus',
    shortDescription: 'Homeostasis, hormone regulation, circadian rhythm',
    system: 'central',
    anchor: mni(5, -2, -12),
    labelOffset: -0.08,
  },
  brain_stem: {
    id: 'brain_stem',
    displayName: 'Brain Stem',
    shortDescription: 'Autonomic function: heart rate, breathing, arousal',
    system: 'central',
    anchor: mni(2, -28, -38),
    labelOffset: 0.04,
  },

  // ── Central: spinal cord ────────────────────────────────────────────────
  spinal_dorsal_horn: {
    id: 'spinal_dorsal_horn',
    displayName: 'Dorsal Horn',
    shortDescription: 'First relay for pain signals entering the spinal cord',
    system: 'central',
    anchor: body(10, -300, -26),
  },
  spinal_ventral_horn: {
    id: 'spinal_ventral_horn',
    displayName: 'Ventral Horn',
    shortDescription: 'Motor neurons that drive skeletal muscle',
    system: 'central',
    anchor: body(10, -230, 6),
  },

  // ── Peripheral and autonomic ────────────────────────────────────────────
  autonomic_ganglia: {
    id: 'autonomic_ganglia',
    displayName: 'Autonomic Ganglia',
    shortDescription: 'Relay stations of the autonomic nervous system',
    system: 'autonomic',
    anchor: body(25, -230, -18),
  },
  sympathetic_chain: {
    id: 'sympathetic_chain',
    displayName: 'Sympathetic Chain',
    shortDescription: 'Fight-or-flight outflow to heart, vessels and glands',
    system: 'autonomic',
    anchor: body(25, -330, -22),
  },
  vagus_nerve: {
    id: 'vagus_nerve',
    displayName: 'Vagus Nerve',
    shortDescription: 'Parasympathetic "rest and digest" outflow to the organs',
    system: 'autonomic',
    anchor: body(24, -250, 45),
  },
  peripheral_sensory: {
    id: 'peripheral_sensory',
    displayName: 'Peripheral Sensory Nerves',
    shortDescription: 'Carry touch, temperature and pain from the body inward',
    system: 'somatic',
    anchor: body(222, -420, 22),
  },
  neuromuscular_junction: {
    id: 'neuromuscular_junction',
    displayName: 'Neuromuscular Junction',
    shortDescription: 'Where motor nerves meet muscle and trigger contraction',
    system: 'somatic',
    anchor: body(100, -930, -10),
  },
  enteric_plexus: {
    id: 'enteric_plexus',
    displayName: 'Enteric Nervous System',
    shortDescription: 'The gut\'s own neural network — the "second brain"',
    system: 'enteric',
    anchor: body(10, -360, 66),
  },
};

export const REGION_IDS = Object.keys(REGIONS) as RegionId[];

/** Region ids grouped by nervous-system subdivision. */
export function regionsBySystem(system: NervousSystem): RegionId[] {
  return REGION_IDS.filter((id) => REGIONS[id].system === system);
}
